import React, { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import moment from 'moment';
import { Questionnaire } from '../../interfaces/questionnaire.interface';

type Props = {
  questionnaires: Questionnaire[];
};

export default function UpcomingArchiveWarning({ questionnaires }: Props) {
  const [dismissed, setDismissed] = useState(false);

  const signature = useMemo(() => (
    (questionnaires || [])
      .map((q) => `${q.id ?? ''}:${q.version}:${q.archiveDate ?? ''}`)
      .join('|')
  ), [questionnaires]);

  useEffect(() => {
    setDismissed(false);
  }, [signature]);

  const { versions, daysUntilArchive, isFuture } = useMemo(() => {
    if (!questionnaires?.length) {
      return { versions: '', daysUntilArchive: null as number | null, isFuture: false };
    }

    const firstArchiveDate = questionnaires[0].archiveDate;
    if (!firstArchiveDate) {
      return { versions: '', daysUntilArchive: null as number | null, isFuture: false };
    }

    const now = moment();
    const archiveMoment = moment(firstArchiveDate);

    const future = archiveMoment.isAfter(now);

    const computedDays = future
      ? Math.max(1, archiveMoment.diff(now, 'days'))
      : null;

    const computedVersions = questionnaires
      .map((q) => q.version.replace('.0', ''))
      .join(', ');

    return { versions: computedVersions, daysUntilArchive: computedDays, isFuture: future };
  }, [questionnaires]);

  if (!questionnaires?.length || dismissed || !isFuture) return null;

  const dayLabel = daysUntilArchive === 1 ? 'day' : 'days';

  return (
    <Alert
      severity="warning"
      onClose={() => setDismissed(true)}
    >
      TPN Questionnaire versions {versions} will be archived in{' '}
      {daysUntilArchive ?? ''} {daysUntilArchive ? dayLabel : ''}. All questions will be locked and read-only.
      Please review and complete all outstanding tasks to ensure uninterrupted progress.
    </Alert>
  );
}
